import { useCallback, useEffect, useRef, useState } from 'react'
import api from '@/api/axiosApi'
import { API_BASE_URL } from '@/config/env'
import { useSSE, type SSEEvent } from './useSSE'
import { useCurrentUser } from './useCurrentUser'
import { useAuthStore } from '@/stores/useAuthStore'
import type {
  PresenceSnapshotPayload,
  PresenceViewer,
  RawPresenceViewer,
} from '@/types/presence'

/** Renew our lease this often; the server expires it after 30s without one. */
const HEARTBEAT_INTERVAL_MS = 10_000
/** No snapshot for this long means we can't trust the list — show nobody. */
const STALE_AFTER_MS = 30_000

function toPresenceViewer(raw: RawPresenceViewer): PresenceViewer {
  return {
    userId: raw.user_id,
    email: raw.email,
    name: raw.name,
    versionId: raw.version_id,
  }
}

/**
 * Drop our lease right away. `sendBeacon` survives page unload and needs no
 * token (the random connId is the capability), so it also works on logout,
 * tab close and URL changes. If it is lost anyway, the lease just expires.
 */
function sendPresenceLeave(planId: string, connId: string): void {
  navigator.sendBeacon(
    `${API_BASE_URL}/plans/${planId}/presence/leave?conn_id=${connId}`
  )
}

/** Whether this page is visible and not being unloaded/frozen into bfcache. */
function usePageActive(): boolean {
  const [active, setActive] = useState(() => !document.hidden)

  useEffect(() => {
    const onVisibility = () => setActive(!document.hidden)
    const onPageHide = () => setActive(false)
    const onPageShow = () => setActive(!document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onPageShow)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
    }
  }, [])

  return active
}

/**
 * Who else has this plan open, and on which version.
 *
 * Presence is a server-side lease that this hook renews with a heartbeat while
 * the plan is open in a visible tab. Every way of leaving (route change, plan or
 * version switch, hiding the tab, closing it, logging out) ends the effect or
 * fires `pagehide`, which sends a leave beacon; anything that skips both
 * (crash, lost network) is covered by the lease expiring.
 *
 * Both the heartbeat response and the SSE stream deliver full snapshots, so
 * the list is always replaced, never patched — a missed event can't leave a
 * ghost avatar behind.
 */
export function usePlanPresence(
  planId: string | undefined,
  versionId: string | undefined
): { viewers: PresenceViewer[] } {
  const [viewers, setViewers] = useState<PresenceViewer[]>([])
  const [lastSnapshotAt, setLastSnapshotAt] = useState(0)
  const { userId: currentUserId } = useCurrentUser()
  // Read through a ref so a late-resolving user id doesn't restart the lease.
  const currentUserIdRef = useRef(currentUserId)
  currentUserIdRef.current = currentUserId
  const pageActive = usePageActive()
  const active = pageActive && !!planId && !!versionId

  const applySnapshot = useCallback((raw: RawPresenceViewer[]) => {
    setViewers(
      raw
        .filter(v => v.user_id !== currentUserIdRef.current)
        .map(toPresenceViewer)
    )
    setLastSnapshotAt(Date.now())
  }, [])

  // One lease per visit. A fresh connId on every run means a leave can never
  // race a later heartbeat for the same id (the server rejects ids it saw leave).
  useEffect(() => {
    setViewers([])
    if (!active || !planId || !versionId) return

    const connId = crypto.randomUUID()
    let stopped = false

    const heartbeat = async () => {
      if (stopped || !useAuthStore.getState().accessToken) return
      try {
        const { data } = await api.post<PresenceSnapshotPayload>(
          `/plans/${planId}/presence/heartbeat`,
          null,
          { params: { version_id: versionId, conn_id: connId } }
        )
        if (!stopped) applySnapshot(data.viewers)
      } catch {
        // Best effort: the next beat retries, the staleness guard clears the list.
      }
    }

    const leave = () => sendPresenceLeave(planId, connId)
    void heartbeat()
    const interval = window.setInterval(heartbeat, HEARTBEAT_INTERVAL_MS)
    window.addEventListener('pagehide', leave)

    return () => {
      stopped = true
      window.clearInterval(interval)
      window.removeEventListener('pagehide', leave)
      leave()
    }
  }, [active, planId, versionId, applySnapshot])

  // If neither the heartbeat nor the stream has delivered a snapshot lately,
  // we are cut off: show nobody rather than a list that may be stale.
  useEffect(() => {
    if (!active) return
    const check = window.setInterval(() => {
      if (Date.now() - lastSnapshotAt > STALE_AFTER_MS) setViewers([])
    }, HEARTBEAT_INTERVAL_MS)
    return () => window.clearInterval(check)
  }, [active, lastSnapshotAt])

  const handleMessage = useCallback(
    (event: SSEEvent) => {
      if (event.type !== 'presence_snapshot') return
      applySnapshot((event.data as unknown as PresenceSnapshotPayload).viewers)
    },
    [applySnapshot]
  )

  useSSE({
    url: active ? `/plans/${planId}/presence/events` : null,
    streamId: active ? `presence:${planId}` : null,
    enabled: active,
    onMessage: handleMessage,
    retry: true,
    retryInterval: 3000,
    maxRetryInterval: 60_000,
    autoDisconnectOnTerminal: false,
    openWhenHidden: false,
  })

  return { viewers }
}
